/**
 * BasemapControl: the background-map switcher, docked on the map itself.
 *
 * Lives on every drawing map (district plans and communities of interest) so
 * the choice is always reversible where it applies. The preference is global
 * and persisted, so a control that is reachable from only one of the two maps
 * can strand the other one on an empty background.
 *
 * Labels collapse to icons below `sm` to protect map area on phones.
 */
import {
  BASEMAP_CHOICES,
  BASEMAP_HINTS,
  BASEMAP_LABELS,
  useBasemapStore,
  type BasemapChoice,
} from '../../stores/basemap';

function ChoiceIcon({ choice }: { choice: BasemapChoice }): JSX.Element {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (choice === 'streets') {
    return (
      <svg {...common}>
        <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z" />
        <path d="M9 4v14M15 6v14" />
      </svg>
    );
  }
  if (choice === 'satellite') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  );
}

export function BasemapControl({ className }: { className?: string }): JSX.Element {
  const choice = useBasemapStore((s) => s.choice);
  const setChoice = useBasemapStore((s) => s.setChoice);

  return (
    <div
      role="radiogroup"
      aria-label="Base map"
      className={`floating flex gap-0.5 p-1 ${className ?? ''}`}
    >
      {BASEMAP_CHOICES.map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={choice === option}
          title={BASEMAP_HINTS[option]}
          onClick={() => setChoice(option)}
          className={`flex h-7 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors ${
            choice === option
              ? 'bg-ink text-white'
              : 'text-stone-600 hover:bg-stone-100 hover:text-ink'
          }`}
        >
          <ChoiceIcon choice={option} />
          <span className="sr-only xl:not-sr-only">{BASEMAP_LABELS[option]}</span>
        </button>
      ))}
    </div>
  );
}
